import React, { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { markBootStage } from "../../../utilities/bootWatchdog";
import { containerLog, CONTAINER_LOG } from "../../../utilities/containerLog";
import { reloadApp } from "../../../utilities/reloadApp";
import { isChunkLoadError } from "../../../utilities/chunkLoadError";

// Holds the time of the last chunk-error reload, not a once-per-tab flag. With
// a flag, a tab that reloaded after one deploy could never reload again, so a
// tab left open across two deploys (store machines run for days) landed on the
// manual screen (support task 41863718). A reload is skipped only if the last
// one was under RELOAD_COOLDOWN_MS ago, i.e. the live build itself fails.
const RELOAD_FLAG = "mrgChunkErrorReloaded";
const RELOAD_COOLDOWN_MS = 60 * 1000;
const SAFE_BOOT_FLAG = "mrgSafeBootTried";

// Rendered by the Sentry.ErrorBoundary wrapping the whole tree — the last line
// of defence against a whitescreen. Must not depend on Redux, translations or
// lazy chunks: any of those may be the thing that just crashed. (The Sentry SDK
// and the leaf utilities below are already loaded and import-free.)
const AppErrorFallback = ({ error }) => {
    const message = String(error?.message ?? error ?? "");
    const isChunkError = isChunkLoadError(error);

    // The boundary has already sent the crash to Sentry; tell the boot sentinel
    // so it does not file the same launch a second time as a hang.
    useEffect(() => {
        markBootStage("error-fallback");
    }, []);

    let willAutoReload = false;
    if (isChunkError) {
        try {
            const lastReloadAt = Number(sessionStorage.getItem(RELOAD_FLAG)) || 0;
            willAutoReload = Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS;
        } catch {
            willAutoReload = false;
        }
    }

    // Safe boot: a non-chunk crash at startup is, in every case diagnosed so
    // far, poisoned persisted Redux state (`royalMomentumState` written by a
    // different bundle version). Clearing it and rebooting once revives the
    // install; the stored login (`appUser`) is deliberately kept. Guarded by a
    // sessionStorage flag so an unrelated persistent crash shows the manual UI
    // instead of reload-looping. Even in containers where reload() resolves
    // blank, the poison is already cleared — the next manual launch boots.
    let willSafeBoot = false;
    if (!isChunkError) {
        try {
            willSafeBoot =
                !sessionStorage.getItem(SAFE_BOOT_FLAG) &&
                localStorage.getItem("royalMomentumState") !== null;
        } catch {
            willSafeBoot = false;
        }
    }

    // A failing chunk usually means the user is holding an index.html from a
    // previous deploy; one reload fetches the new manifest. The sessionStorage
    // timestamp stops a genuinely broken deploy from reload-looping.
    useEffect(() => {
        if (!willAutoReload && !willSafeBoot) return;
        try {
            if (willSafeBoot) {
                sessionStorage.setItem(SAFE_BOOT_FLAG, "1");
                localStorage.removeItem("royalMomentumState");
            } else {
                sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
            }
        } catch {
            return;
        }
        containerLog(
            `${willSafeBoot ? "Safe boot" : "Chunk-error reload"} - clearing ` +
                `${willSafeBoot ? "royalMomentumState" : "nothing"} and reloading after: ${message}`,
            CONTAINER_LOG.WARN
        );
        // The boundary's captureException is still in the transport queue; a
        // reload right now can drop it — and this is the one crash we most need
        // to see. Wait for the flush (bounded), then reboot either way.
        const reboot = () => reloadApp(willSafeBoot ? "safe-boot" : "chunk-error");
        Sentry.flush(2000).then(reboot, reboot);
    }, [willAutoReload, willSafeBoot, message]);

    if (willAutoReload || willSafeBoot) return null;

    let isSpanish = false;
    try {
        isSpanish =
            (window.currentLanguage || localStorage.getItem("mrgCurrentLanguage")) === "es";
    } catch {
        isSpanish = false;
    }

    return (
        <div className="w-screen h-screen fixed top-0 left-0 z-[9999] flex items-center justify-center bg-white px-6">
            <div className="max-w-md w-full text-center">
                <h1 className="text-2xl font-bold text-[#0C214D] mb-3">
                    {isSpanish ? "Algo salió mal" : "Something went wrong"}
                </h1>
                <p className="text-base text-[#0C214D] opacity-80 mb-6">
                    {isSpanish
                        ? "Se ha producido un error inesperado. Por favor, recarga la página para continuar."
                        : "An unexpected error occurred. Please reload the page to continue."}
                </p>
                <button
                    type="button"
                    onClick={() => reloadApp("user-retry")}
                    className="bg-[#0C214D] text-white font-semibold rounded-lg px-6 py-3"
                >
                    {isSpanish ? "Recargar página" : "Reload page"}
                </button>
            </div>
        </div>
    );
};

export default AppErrorFallback;
